import { createContext, useContext } from 'react';
import { z } from 'zod';
import { componentSchema } from '@/config/schema';
import { useStableIds } from '@/hooks/useStableIds';
import { warnOnce } from '@/lib/warnOnce';
import type { WidgetProps } from '@/widgets/registry';
import { WidgetSlot } from '@/widgets/WidgetSlot';

/**
 * A container widget: renders `options.components` — the same node shape as the top-level
 * `components` array — as equal-width columns. Port of legacy `groupWidget.vue`.
 *
 * It is a registered widget like any other, which is what lets it nest: a `GroupWidget`'s
 * `options.components` may itself contain a `GroupWidget`.
 *
 * No deployed config uses it today. It is ported because the legacy app registered it and a
 * config can start using it without a code change.
 */

/**
 * `options.components` — an array of ordinary component nodes.
 *
 * Reuses `componentSchema` rather than redeclaring the node shape, so a change to the config
 * format cannot leave nested components parsing by different rules than top-level ones.
 */
const groupOptionsSchema = z.object({
  components: z.array(componentSchema),
});

/**
 * How many `GroupWidget`s deep we are. Top-level widgets sit at 0.
 *
 * The cap exists for a config-authoring mistake, not for any config that exists: a group
 * whose `options.components` contains itself would recurse until the tab dies, and on a
 * wall-mounted display that is indistinguishable from a hardware fault. Three levels is far
 * past anything the layout could usefully express.
 */
export const MAX_GROUP_DEPTH = 3;

const GroupDepthContext = createContext(0);

export function GroupWidget({ componentId, options }: WidgetProps) {
  const depth = useContext(GroupDepthContext);
  const getId = useStableIds();
  const parsed = groupOptionsSchema.safeParse(options);

  if (depth >= MAX_GROUP_DEPTH) {
    warnOnce(
      `group-depth:${componentId}`,
      '[signage] GroupWidget nested past the depth limit of %d; rendering nothing below it. ' +
        'This is a config authoring mistake — check `options.components` for a group that ' +
        'contains itself.',
      MAX_GROUP_DEPTH,
    );
    return null;
  }

  // A malformed group costs its own subtree, never the page (CLAUDE.md constraint 4). Legacy
  // was laxer — `Array.isArray(props.options.components)` and no per-node checking — so a
  // node missing `type` reached Vue's dynamic component resolver as `undefined`. Here it
  // fails the schema and the whole group is skipped, with the reason in the console.
  if (!parsed.success) {
    warnOnce(
      `group-options:${componentId}`,
      '[signage] GroupWidget options rejected; rendering nothing. %s',
      z.prettifyError(parsed.error),
    );
    return null;
  }

  return (
    <GroupDepthContext value={depth + 1}>
      {/*
       * Legacy `.group-container` / `.group-container-item`, whose SCSS was: column by
       * default, and from `lg` a row with `gap: 1rem` and `flex: 1 1 0` + `width: 0` per
       * item. Expressed as utilities here (`lg:gap-4` is 1rem on Tailwind 4's 0.25rem
       * spacing scale — deliberately not `lg:gap-1`, which is 0.25rem despite the unrelated
       * `gap-1` override in `index.css`).
       *
       * `lg:w-0` is the load-bearing one: `flex-1` alone lets a long text widget claim more
       * than its share of the row, because `flex-basis: 0` still leaves `width: auto` as the
       * min-content floor. Zero width forces genuinely equal columns.
       *
       * The two legacy class names are kept as markers with no CSS attached — they cost
       * nothing and they make a DOM diff against the legacy app (SYM-506) readable.
       */}
      <div className="group-container flex flex-col lg:flex-row lg:gap-4">
        {parsed.data.components.map((component, index) => {
          const childId = getId(index);
          return (
            <div className="group-container-item flex-1 lg:w-0" key={childId}>
              <WidgetSlot component={component} componentId={childId} />
            </div>
          );
        })}
      </div>
    </GroupDepthContext>
  );
}
